import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Grid,
  LinearProgress,
  CircularProgress,
  Avatar,
} from "@mui/material";
import {
  Description as EntriesIcon,
  Folder as DocumentsIcon,
  Link as LinkIcon,
  LocalShipping as SuppliersIcon,
  CloudUpload as UploadIcon,
  Add as AddIcon,
  Assessment as AuditIcon,
  ChevronRight as ChevronRightIcon,
  Calculate as CalculateIcon,
  CheckCircleOutline as CheckIcon,
  Inventory as ProductsIcon,
} from "@mui/icons-material";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { useGetCustomsDocumentsQuery, useGetCustomsClustersQuery, useGetEntryLinkingStatsQuery } from "@/services/customs-document/customs-document-api";
import { useGetDashboardManufacturersQuery } from "@/services";
import { useGetProductsWithTariffsQuery } from "@/services/product-catalogue/pc-api";
import UploadCustomsDocuments from "../../documents/upload-customs-documents";
import { tokens } from "@/tokens";

// Path map for tab navigation
const TAB_PATHS: Record<number, string> = {
  1: '/customs-erp/products',
  2: '/customs-erp/suppliers',
  3: '/customs-erp/entries',
  4: '/customs-erp/documents',
};

const DashboardTab: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);

  const [showUploadModal, setShowUploadModal] = useState(false);

  const isTDAdmin = user?.userRole === roles.TDADMIN;
  const isTDAdminTCD = isTDAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";

  const shouldSkipQueries = useMemo(() => {
    if (!user) return true;
    const isTDClient = user.userRole === roles.TDCLIENT;
    const isSPRole = [roles.SPOWNER, roles.SPPARTNER, roles.SPCLIENT].includes(user.userRole as any);
    if (isTDAdminTCD) return false;
    if (isTDClient) return false;
    if (isSPRole && !selectedClient) return true;
    return !selectedClient;
  }, [user, selectedClient, isTDAdminTCD]);

  const clientId = useMemo(() => {
    if (isTDAdminTCD) return selectedTenant?._id;
    return selectedClient?._id;
  }, [isTDAdminTCD, selectedTenant, selectedClient]);

  const { data: documentsResponse, isLoading: isLoadingDocs } = useGetCustomsDocumentsQuery(
    { clientId, tenantId: isTDAdmin ? selectedTenant?._id : undefined, pageNo: 1, pageSize: 50 },
    { skip: shouldSkipQueries }
  );

  const { data: clustersResponse, isLoading: isLoadingClusters } = useGetCustomsClustersQuery(
    { clientId, tenantId: isTDAdmin ? selectedTenant?._id : undefined, pageNo: 1, pageSize: 50 },
    { skip: shouldSkipQueries }
  );

  const { data: manufacturersData, isLoading: isLoadingManufacturers } = useGetDashboardManufacturersQuery(
    { dashboardVisibility: { clientId, ...(isTDAdminTCD && selectedTenant && { tenantId: selectedTenant._id }) } },
    { skip: shouldSkipQueries }
  );

  const { data: entryLinkingStats, isLoading: isLoadingLinkingStats } = useGetEntryLinkingStatsQuery(
    { clientId, tenantId: isTDAdmin ? selectedTenant?._id : undefined },
    { skip: shouldSkipQueries }
  );

  const { data: productsData, isLoading: isLoadingProducts } = useGetProductsWithTariffsQuery(
    { effectiveDate: new Date().toISOString().split('T')[0], pageNo: 1, pageSize: 1, ...(clientId ? { clientId } : {}), ...(isTDAdminTCD && { tenantId: selectedTenant?._id }) },
    { skip: shouldSkipQueries }
  );

  const isLoading = isLoadingDocs || isLoadingClusters || isLoadingManufacturers || isLoadingLinkingStats || isLoadingProducts;

  // Derive simple counts from paginated responses; heavy stats come from entryLinkingStats
  const metrics = useMemo(() => {
    const documents = documentsResponse?.data || [];
    const documentsTotal = documentsResponse?.countDocs || documents.length;
    const clusters = clustersResponse?.data || [];
    const clustersTotal = clustersResponse?.countDocs || clusters.length;
    const manufacturers = manufacturersData?.data || [];

    // Count clusters needing audit (no audit status) from current page
    let clustersNeedingAudit = 0;
    clusters.forEach((cluster: any) => {
      if (!cluster.latestAuditStatus) clustersNeedingAudit++;
    });

    return {
      documents: documentsTotal,
      clusters: clustersTotal,
      suppliers: manufacturers.length,
      clustersNeedingAudit,
    };
  }, [documentsResponse, clustersResponse, manufacturersData]);

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 300, p: 3 }}>
        <CircularProgress sx={{ color: tokens.brand.primary }} size={32} />
        <Typography variant="body2" sx={{ mt: 2, color: tokens.text.secondary }}>
          Loading dashboard...
        </Typography>
      </Stack>
    );
  }

  // Action item style
  const actionItemSx = {
    p: 1.5,
    borderRadius: 1,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': {
      bgcolor: '#F1F5F9',
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      <Stack spacing={{ xs: 1.5, md: 2 }}>
        {/* Top Row - Action Items + Metrics */}
        <Grid container spacing={{ xs: 1, md: 1.5 }}>
          {/* Left - Action Items */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 2, border: `1px solid ${tokens.border.default}`, borderRadius: 1, height: '100%' }}>
              <Typography variant="subtitle2" fontWeight={tokens.fontWeight.bold} color={tokens.text.primary} sx={{ mb: 1.5 }}>
                Action Items
              </Typography>

              <Stack spacing={0.5}>
                {((entryLinkingStats?.totalEntries ?? 0) - (entryLinkingStats?.linkedEntries ?? 0)) > 0 && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={actionItemSx}
                    onClick={() => setShowUploadModal(true)}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar sx={{ bgcolor: `${tokens.brand.primary}12`, width: 32, height: 32 }}>
                        <UploadIcon sx={{ color: tokens.brand.primary, fontSize: 16 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={tokens.fontWeight.semibold} color={tokens.text.primary}>
                          Upload more documents
                        </Typography>
                        <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                          {(entryLinkingStats?.totalEntries ?? 0) - (entryLinkingStats?.linkedEntries ?? 0)} entries still need documentation
                        </Typography>
                      </Box>
                    </Stack>
                    <ChevronRightIcon sx={{ color: tokens.text.muted, fontSize: 20 }} />
                  </Stack>
                )}

                {(entryLinkingStats?.missingProducts ?? 0) > 0 && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={actionItemSx}
                    onClick={() => navigate(TAB_PATHS[1])}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar sx={{ bgcolor: `${tokens.status.success}12`, width: 32, height: 32 }}>
                        <AddIcon sx={{ color: tokens.status.success, fontSize: 16 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={tokens.fontWeight.semibold} color={tokens.text.primary}>
                          Create more products
                        </Typography>
                        <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                          We detected {entryLinkingStats?.missingProducts ?? 0} products missing from your catalog
                        </Typography>
                      </Box>
                    </Stack>
                    <ChevronRightIcon sx={{ color: tokens.text.muted, fontSize: 20 }} />
                  </Stack>
                )}

                {metrics.clustersNeedingAudit > 0 && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={actionItemSx}
                    onClick={() => navigate(TAB_PATHS[4])}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar sx={{ bgcolor: `${tokens.status.warning}12`, width: 32, height: 32 }}>
                        <AuditIcon sx={{ color: tokens.status.warning, fontSize: 16 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={tokens.fontWeight.semibold} color={tokens.text.primary}>
                          Run document audits
                        </Typography>
                        <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                          {metrics.clustersNeedingAudit} document clusters haven't been audited
                        </Typography>
                      </Box>
                    </Stack>
                    <ChevronRightIcon sx={{ color: tokens.text.muted, fontSize: 20 }} />
                  </Stack>
                )}

                {((entryLinkingStats?.totalEntries ?? 0) - (entryLinkingStats?.linkedEntries ?? 0)) === 0 &&
                  (entryLinkingStats?.missingProducts ?? 0) === 0 &&
                  metrics.clustersNeedingAudit === 0 && (
                  <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 3 }}>
                    <CheckIcon sx={{ color: tokens.text.muted, fontSize: 28 }} />
                    <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                      No pending action items
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Grid>

          {/* Right - Metrics List */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 2, border: `1px solid ${tokens.border.default}`, borderLeft: `3px solid ${tokens.brand.primary}`, borderRadius: 1, height: '100%' }}>
              <Typography variant="caption" fontWeight={tokens.fontWeight.bold} color={tokens.text.secondary} sx={{ mb: 1.5, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Key Metrics
              </Typography>

              <Stack spacing={0.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.75, px: 0.5, borderRadius: 1, cursor: 'pointer', transition: 'all 0.15s ease', '&:hover': { bgcolor: tokens.bg.hover } }} onClick={() => navigate(TAB_PATHS[3])}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: tokens.bg.muted, width: 28, height: 28 }}>
                      <EntriesIcon sx={{ color: tokens.text.secondary, fontSize: 15 }} />
                    </Avatar>
                    <Typography variant="body2" color={tokens.text.secondary}>Entries</Typography>
                  </Stack>
                  <Typography variant="subtitle2" fontWeight={tokens.fontWeight.heavy} color={tokens.text.primary}>
                    {(entryLinkingStats?.totalEntries ?? 0).toLocaleString()}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.75, px: 0.5, borderRadius: 1, cursor: 'pointer', transition: 'all 0.15s ease', '&:hover': { bgcolor: tokens.bg.hover } }} onClick={() => navigate(TAB_PATHS[1])}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: tokens.bg.muted, width: 28, height: 28 }}>
                      <ProductsIcon sx={{ color: tokens.text.secondary, fontSize: 15 }} />
                    </Avatar>
                    <Typography variant="body2" color={tokens.text.secondary}>Products</Typography>
                  </Stack>
                  <Typography variant="subtitle2" fontWeight={tokens.fontWeight.heavy} color={tokens.text.primary}>
                    {(productsData?.meta?.countDocs ?? 0).toLocaleString()}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.75, px: 0.5, borderRadius: 1, cursor: 'pointer', transition: 'all 0.15s ease', '&:hover': { bgcolor: tokens.bg.hover } }} onClick={() => navigate(TAB_PATHS[2])}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: tokens.bg.muted, width: 28, height: 28 }}>
                      <SuppliersIcon sx={{ color: tokens.text.secondary, fontSize: 15 }} />
                    </Avatar>
                    <Typography variant="body2" color={tokens.text.secondary}>Suppliers</Typography>
                  </Stack>
                  <Typography variant="subtitle2" fontWeight={tokens.fontWeight.heavy} color={tokens.text.primary}>
                    {(metrics.suppliers ?? 0).toLocaleString()}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.75, px: 0.5, borderRadius: 1, cursor: 'pointer', transition: 'all 0.15s ease', '&:hover': { bgcolor: tokens.bg.hover } }} onClick={() => navigate(TAB_PATHS[4])}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: tokens.bg.muted, width: 28, height: 28 }}>
                      <DocumentsIcon sx={{ color: tokens.text.secondary, fontSize: 15 }} />
                    </Avatar>
                    <Typography variant="body2" color={tokens.text.secondary}>Documents</Typography>
                  </Stack>
                  <Typography variant="subtitle2" fontWeight={tokens.fontWeight.heavy} color={tokens.text.primary}>
                    {(metrics.documents ?? 0).toLocaleString()}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* Middle Row - Progress & Quick Actions */}
        <Grid container spacing={{ xs: 1, md: 1.5 }}>
          {/* Entry-Document Linking Progress */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, border: `1px solid ${tokens.border.default}`, borderRadius: 1, height: '100%' }}>
              <Typography variant="subtitle2" fontWeight={tokens.fontWeight.bold} color={tokens.text.primary} sx={{ mb: 2 }}>
                Entry-Document Linking
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LinkIcon sx={{ fontSize: 16, color: tokens.brand.primary }} />
                    <Typography variant="body2" color={tokens.text.primary}>Entries Linked</Typography>
                  </Stack>
                  <Typography variant="body2" fontWeight={tokens.fontWeight.bold} color={tokens.brand.primary}>
                    {entryLinkingStats && entryLinkingStats.totalEntries > 0
                      ? Math.round((entryLinkingStats.linkedEntries / entryLinkingStats.totalEntries) * 100)
                      : 0}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={entryLinkingStats && entryLinkingStats.totalEntries > 0
                    ? Math.round((entryLinkingStats.linkedEntries / entryLinkingStats.totalEntries) * 100)
                    : 0}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    bgcolor: tokens.border.default,
                    '& .MuiLinearProgress-bar': { bgcolor: tokens.brand.primary, borderRadius: 1 }
                  }}
                />
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: tokens.text.secondary }}>
                  {(entryLinkingStats?.linkedEntries ?? 0).toLocaleString()} of {(entryLinkingStats?.totalEntries ?? 0).toLocaleString()} entries have documents
                </Typography>
              </Box>

              {/* Entry Audit Results */}
              <Box>
                <Typography variant="body2" fontWeight={tokens.fontWeight.semibold} color={tokens.text.primary} sx={{ mb: 1 }}>
                  Entry Audit Results
                </Typography>
                <Stack spacing={0.75}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tokens.status.success }} />
                      <Typography variant="caption" color={tokens.text.body}>Approved</Typography>
                    </Stack>
                    <Typography variant="caption" fontWeight={tokens.fontWeight.bold} color={tokens.text.primary}>
                      {(entryLinkingStats?.auditResults.approved ?? 0).toLocaleString()}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tokens.status.warning }} />
                      <Typography variant="caption" color={tokens.text.body}>Needs Review</Typography>
                    </Stack>
                    <Typography variant="caption" fontWeight={tokens.fontWeight.bold} color={tokens.text.primary}>
                      {(entryLinkingStats?.auditResults.needsReview ?? 0).toLocaleString()}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tokens.status.danger }} />
                      <Typography variant="caption" color={tokens.text.body}>Discrepancies</Typography>
                    </Stack>
                    <Typography variant="caption" fontWeight={tokens.fontWeight.bold} color={tokens.text.primary}>
                      {(entryLinkingStats?.auditResults.discrepancies ?? 0).toLocaleString()}
                    </Typography>
                  </Stack>
                  {(entryLinkingStats?.auditResults.notAudited ?? 0) > 0 && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tokens.text.muted }} />
                        <Typography variant="caption" color={tokens.text.body}>Not Audited</Typography>
                      </Stack>
                      <Typography variant="caption" fontWeight={tokens.fontWeight.bold} color={tokens.text.primary}>
                        {(entryLinkingStats?.auditResults.notAudited ?? 0).toLocaleString()}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
                <Typography variant="caption" sx={{ mt: 1, display: 'block', color: tokens.text.secondary }}>
                  {((entryLinkingStats?.auditResults.approved ?? 0) + (entryLinkingStats?.auditResults.needsReview ?? 0) + (entryLinkingStats?.auditResults.discrepancies ?? 0)).toLocaleString()} of {(entryLinkingStats?.linkedEntries ?? 0).toLocaleString()} linked entries audited
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, border: `1px solid ${tokens.border.default}`, borderRadius: 1, height: '100%' }}>
              <Typography variant="subtitle2" fontWeight={tokens.fontWeight.bold} color={tokens.text.primary} sx={{ mb: 2 }}>
                Quick Actions
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Box
                    onClick={() => {
                      navigate(TAB_PATHS[1]);
                      setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('td:open-create-product'));
                      }, 100);
                    }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: { xs: 1.5, md: 2.5 },
                      px: { xs: 1.5, md: 2 },
                      bgcolor: tokens.bg.muted,
                      border: `1px solid ${tokens.border.light}`,
                      borderRadius: 1,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': { borderColor: tokens.border.default, bgcolor: tokens.bg.hover, transform: 'translateY(-1px)' }
                    }}
                  >
                    <AddIcon sx={{ fontSize: 24, color: tokens.brand.primary, mb: 1 }} />
                    <Typography variant="body2" color={tokens.text.primary} fontWeight={tokens.fontWeight.semibold}>
                      Add Product
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    onClick={() => setShowUploadModal(true)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: { xs: 1.5, md: 2.5 },
                      px: { xs: 1.5, md: 2 },
                      bgcolor: tokens.bg.muted,
                      border: `1px solid ${tokens.border.light}`,
                      borderRadius: 1,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': { borderColor: tokens.border.default, bgcolor: tokens.bg.hover, transform: 'translateY(-1px)' }
                    }}
                  >
                    <UploadIcon sx={{ fontSize: 24, color: tokens.brand.primary, mb: 1 }} />
                    <Typography variant="body2" color={tokens.text.primary} fontWeight={tokens.fontWeight.semibold}>
                      Audit/Upload Docs
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    onClick={() => navigate(TAB_PATHS[3])}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: { xs: 1.5, md: 2.5 },
                      px: { xs: 1.5, md: 2 },
                      bgcolor: tokens.bg.muted,
                      border: `1px solid ${tokens.border.light}`,
                      borderRadius: 1,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': { borderColor: tokens.border.default, bgcolor: tokens.bg.hover, transform: 'translateY(-1px)' }
                    }}
                  >
                    <EntriesIcon sx={{ fontSize: 24, color: tokens.brand.primary, mb: 1 }} />
                    <Typography variant="body2" color={tokens.text.primary} fontWeight={tokens.fontWeight.semibold}>
                      View Entries
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    onClick={() => navigate('/tools')}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: { xs: 1.5, md: 2.5 },
                      px: { xs: 1.5, md: 2 },
                      bgcolor: tokens.bg.muted,
                      border: `1px solid ${tokens.border.light}`,
                      borderRadius: 1,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': { borderColor: tokens.border.default, bgcolor: tokens.bg.hover, transform: 'translateY(-1px)' }
                    }}
                  >
                    <CalculateIcon sx={{ fontSize: 24, color: tokens.brand.primary, mb: 1 }} />
                    <Typography variant="body2" color={tokens.text.primary} fontWeight={tokens.fontWeight.semibold}>
                      Duty Calc
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

      </Stack>

      {/* Upload Modal */}
      <UploadCustomsDocuments
        show={showUploadModal}
        setShow={setShowUploadModal}
        tenantId={isTDAdminTCD && selectedTenant ? selectedTenant._id : undefined}
        clientId={selectedClient?._id}
      />
    </Box>
  );
};

export default DashboardTab;
